import { Fragment, useContext, useEffect, useState } from 'react';
import {
  DevHubAlert,
  DevHubButton,
  DevHubChip,
  DevHubFilter,
  DevHubGrid,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { Progress } from '@backstage/core-components';
import { CloudAccountTile } from './CloudAccountTile';
import { AssociateAccount } from './AssociateAccount';
import { useQuery } from '@apollo/client';
import { useAppId } from '../../../contexts/SystemContext';
import {
  AssociateAccountType,
  CloudAccountType,
} from '../types/CloudAccountTypes';
import { UserContext, useUserRoles } from '../../../contexts/UserContext';
import {
  GetCloudAccountsAndRolesForApplication,
  GetCloudAccountsForUser,
} from './CloudAccountQueries';
import { CSVLink } from 'react-csv';

/**
 * A component that displays the cloud accounts associated with an application.
 */
export const ApplicationCloudAccounts = () => {
  const { login, canEdit } = useContext(UserContext);
  const [cloudAccounts, setCloudAccounts] = useState<CloudAccountType[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const appId = useAppId();
  const [open, setOpen] = useState<boolean>(false);

  const { loading, error, data } = useQuery<{
    resources: {
      application: {
        cloudAccounts: CloudAccountType[];
        name: string;
        status: string;
        owner: {
          displayName: string;
        };
      };
    };
  }>(GetCloudAccountsAndRolesForApplication, {
    errorPolicy: 'all',
    variables: {
      applicationId: appId,
    },
  });

  const { value: userRoleData, loading: rolesLoading } = useUserRoles(
    useAppId(),
    login,
  );

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const {
    loading: userAccountsLoading,
    error: userAccountsError,
    data: userAccounts,
  } = useQuery<{ identity: { user: { cloudAccounts: CloudAccountType[] } } }>(
    GetCloudAccountsForUser,
    {
      errorPolicy: 'all',
      variables: {
        login: login,
      },
    },
  );

  useEffect(() => {
    const accounts = data?.resources?.application?.cloudAccounts || [];
    setCloudAccounts(accounts);
  }, [data]);

  if (loading || userAccountsLoading || rolesLoading) {
    return <Progress />;
  }

  if ((error || userAccountsError) && !data?.resources?.application) {
    return (
      <DevHubAlert severity="error">
        Error loading cloud account data
      </DevHubAlert>
    );
  }

  const sortedCloudAccounts = cloudAccounts?.slice().sort((a, b) => {
    const nameA = a?.name?.toLowerCase();
    const nameB = b?.name?.toLowerCase();
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  });

  const availablePlatforms = Array.from(
    new Set(
      cloudAccounts
        ?.map(account => account.platform?.toUpperCase())
        .filter(Boolean),
    ),
  ).sort();

  const filteredCloudAccounts =
    selectedPlatforms.length > 0
      ? sortedCloudAccounts.filter(account =>
          selectedPlatforms.includes(account.platform?.toUpperCase()),
        )
      : sortedCloudAccounts;

  const handlePlatformChange = (platform: string | string[]) => {
    if (Array.isArray(platform)) {
      setSelectedPlatforms(platform);
    } else {
      setSelectedPlatforms(prev =>
        prev.includes(platform)
          ? prev.filter(p => p !== platform)
          : [...prev, platform],
      );
    }
  };

  const handlePlatformDelete = (platformToDelete: string) => {
    setSelectedPlatforms(prev =>
      prev.filter(platform => platform !== platformToDelete),
    );
  };

  const technicalOwner = data?.resources?.application?.owner.displayName;
  const associatedApplications = data?.resources?.application?.name;

  const downloadData = [
    [
      'Account name',
      'Account ID',
      'Platform',
      'Environment',
      'Technical owner',
      'Associated applications',
    ],
    ...filteredCloudAccounts.map(cloudAccount => [
      cloudAccount.name,
      cloudAccount.id,
      cloudAccount.platform,
      cloudAccount.environment,
      technicalOwner,
      associatedApplications,
    ]),
  ];

  return (
    <Fragment>
      <DevHubGrid container spacing={2} alignItems="center" tbMargin="xsmall">
        <DevHubGrid item style={{ marginLeft: 'auto' }}>
          <DevHubFilter
            selectedItems={selectedPlatforms}
            list={availablePlatforms}
            title="Filter By Platform"
            height="medium"
            handleChange={handlePlatformChange}
            useStatusColor={false}
          />
        </DevHubGrid>
        <DevHubGrid item>
          <CSVLink
            filename={`PublicCloudAccounts_${appId}`}
            data={downloadData}
          >
            <DevHubButton variant="contained" color="primary">
              Export
            </DevHubButton>
          </CSVLink>
        </DevHubGrid>
      </DevHubGrid>
      {selectedPlatforms.length > 0 && (
        <DevHubGrid container alignItems="center" spacing={1} margin="small">
          <DevHubGrid item>
            <DevHubTypography>Platform:</DevHubTypography>
          </DevHubGrid>
          {selectedPlatforms.map((platform: string) => (
            <DevHubGrid item key={platform}>
              <DevHubChip
                label={platform}
                onDelete={() => handlePlatformDelete(platform)}
              />
            </DevHubGrid>
          ))}
        </DevHubGrid>
      )}
      <DevHubGrid container spacing={2} tbPadding="medium">
        <DevHubGrid item xs={12} md={6} lg={4} xl={3}>
          <AssociateAccount
            appId={appId}
            cloudAccountsList={cloudAccounts}
            setCloudAccountsList={setCloudAccounts}
            buttonText={(() => {
              if (!canEdit(userRoleData)) {
                return 'Do not have required role to add cloud account connections';
              } else if (data?.resources?.application?.status === 'Retired') {
                return 'Cannot add cloud account connections to retired applications';
              }
              return 'Associate Cloud Account';
            })()}
            userAccounts={userAccounts?.identity?.user?.cloudAccounts!}
            disabled={
              !canEdit(userRoleData) ||
              data?.resources?.application?.status === 'Retired'
            }
            open={open}
            handleOpen={handleOpen}
            handleClose={handleClose}
            type={AssociateAccountType.CARD}
          />
        </DevHubGrid>
        {filteredCloudAccounts?.length ? (
          filteredCloudAccounts.map((cloudAccount: CloudAccountType) => (
            <DevHubGrid
              item
              xs={12}
              md={6}
              lg={4}
              xl={3}
              key={cloudAccount?.id}
            >
              <CloudAccountTile
                applicationId={appId}
                appName={data?.resources?.application?.name}
                cloudAccount={cloudAccount}
                cloudAccountsQuery={GetCloudAccountsAndRolesForApplication}
                userAccounts={userAccounts?.identity?.user?.cloudAccounts!}
                disabled={!canEdit(userRoleData)}
              />
            </DevHubGrid>
          ))
        ) : (
          <DevHubAlert severity="info">
            {selectedPlatforms.length > 0
              ? 'No cloud accounts found for the selected platform(s).'
              : 'No cloud accounts found.'}
          </DevHubAlert>
        )}
      </DevHubGrid>
    </Fragment>
  );
};
